import React, { useState } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { LanguageSelect } from '../common/LanguageSelect';
import apiClient from '../../services/api';
import config from '../../config/config';
import { DEFAULT_LANGUAGE } from '../../config/languages';
import { useAuth } from '../../contexts/AuthContext';

const STARTER_WORDS = ['improve', 'curious', 'challenge'];

const PREDEFINED_INTERESTS = [
  'Soccer', 'Music', 'Programming', 'Travel', 'Cooking',
  'Reading', 'Gaming', 'Art', 'Fitness', 'Photography',
  'Movies', 'Science', 'History', 'Fashion', 'Business'
];

export const InterestsModal = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [interestsList, setInterestsList] = useState([]);
  const [nativeLanguage, setNativeLanguage] = useState(DEFAULT_LANGUAGE);
  const [isLoading, setIsLoading] = useState(false);
  const [isSeedingWords, setIsSeedingWords] = useState(false);
  const [seedingDone, setSeedingDone] = useState(false);
  const [error, setError] = useState('');

  const handleInterestClick = (interest) => {
    if (interestsList.includes(interest)) return;
    const newList = [...interestsList, interest];
    if (newList.join(', ').length <= config.validation.maxInterestsLength) {
      setInterestsList(newList);
    }
  };

  const handleRemoveInterest = (interest) => {
    setInterestsList(interestsList.filter(i => i !== interest));
  };

  const handleSubmit = async () => {
    // Validate native language (required)
    if (!nativeLanguage) {
      setError('Please select your native language');
      return;
    }

    // Interests can be empty (optional)
    if (interestsList.join(', ').length > config.validation.maxInterestsLength) {
      setError(`Interests must be ${config.validation.maxInterestsLength} characters or less`);
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      await apiClient.createInterests(
        interestsList.join(', '),
        user?.email || '',
        user?.name || '',
        nativeLanguage
      );

      // Seed starter words — show a message while generating
      setIsLoading(false);
      setIsSeedingWords(true);
      await Promise.all(STARTER_WORDS.map(word => apiClient.createWord(word, true)));
      setSeedingDone(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save profile');
      setIsSeedingWords(false);
      setIsLoading(false);
    }
  };

  if (isSeedingWords) {
    return (
      <Modal isOpen={isOpen} onClose={undefined} title="Almost there!" size="md">
        <div className="py-6 text-center space-y-4">
          {!seedingDone ? (
            <>
              <div className="flex justify-center">
                <svg className="animate-spin h-10 w-10 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
              <p className="text-lg font-medium text-gray-800">Generating your first words…</p>
              <p className="text-sm text-gray-500 max-w-xs mx-auto">
                We're creating personalized examples for <span className="font-medium text-gray-700">{STARTER_WORDS.join(', ')}</span> based on your interests. This takes a few seconds.
              </p>
            </>
          ) : (
            <>
              <div className="flex justify-center text-4xl">🎉</div>
              <p className="text-lg font-medium text-gray-800">Your first words are ready!</p>
              <p className="text-sm text-gray-500 max-w-xs mx-auto">
                We've added <span className="font-medium text-gray-700">{STARTER_WORDS.join(', ')}</span> to get you started. Now it's your turn to grow your vocabulary!
              </p>
              <div className="pt-2">
                <Button onClick={() => { onSuccess?.(); onClose(); }}>
                  Let's go!
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={!isLoading ? onClose : undefined} title="Complete Your Profile" size="md">
      <div className="space-y-4">
        <p className="text-gray-600">
          Tell us about yourself to get personalized vocabulary examples.
        </p>

        {/* NATIVE LANGUAGE DROPDOWN */}
        <LanguageSelect
          label="Native Language"
          value={nativeLanguage}
          onChange={(e) => setNativeLanguage(e.target.value)}
          required
          error={error && !nativeLanguage ? 'Native language is required' : ''}
        />

        {/* INTERESTS - Tag List */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Your Interests (Optional)</label>
          {interestsList.length > 0 ? (
            <div className="flex flex-wrap gap-2 mb-3">
              {interestsList.map((interest) => (
                <span
                  key={interest}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                >
                  {interest}
                  <button
                    type="button"
                    onClick={() => handleRemoveInterest(interest)}
                    disabled={isLoading}
                    className="ml-1 text-blue-500 hover:text-blue-800 focus:outline-none leading-none"
                    aria-label={`Remove ${interest}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic mb-3">No interests added yet.</p>
          )}
        </div>

        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">Quick Add:</p>
          <div className="flex flex-wrap gap-2">
            {PREDEFINED_INTERESTS.map((interest) => (
              <button
                key={interest}
                type="button"
                onClick={() => handleInterestClick(interest)}
                className={`px-3 py-1 text-sm rounded-full transition-colors ${
                  interestsList.includes(interest)
                    ? 'bg-blue-100 text-blue-500 cursor-default'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
                disabled={isLoading || interestsList.includes(interest)}
              >
                {interestsList.includes(interest) ? interest : `+ ${interest}`}
              </button>
            ))}
          </div>
        </div>

        {error && !nativeLanguage && (
          <p className="text-sm text-red-500">{error}</p>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading || !nativeLanguage}>
            {isLoading ? 'Saving...' : 'Save Profile'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
